import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Shield, Check } from '../components/Icons.jsx';

const cards = [
  {
    domain: 'Confidentiality',
    scenario: 'A patient in A&E is brought in unconscious after a fall. His wife arrives and demands to know his blood alcohol level, saying she suspects he has a drinking problem and needs the information to help him.',
    options: [
      'Share the result — she is family and it may help his care.',
      'Refuse to share, explain confidentiality applies even to next-of-kin.',
      'Tell the wife to ask the patient herself when he regains consciousness.',
      'Ask a senior colleague to handle the conversation.',
    ],
    answer: 1,
    rationale: 'Confidentiality does not end because a patient is unconscious. Information can only be shared with third parties without consent if it is necessary to protect the patient or others from serious harm — which "helping him with a drinking problem" does not meet. Option B is very appropriate.',
    gmc: 'GMC Domain 4 — maintaining trust and confidentiality.',
    mistake: 'Students often pick A thinking proximity of care justifies disclosure. It does not.',
    script: '"What does the GMC say about relatives asking for information? Is being family automatic grounds for disclosure? What would change your answer — if she said he was suicidal, for example?"',
  },
  {
    domain: 'Clinical uncertainty',
    scenario: 'An FY1 prescribes a drug at 3 am after checking an online reference. The dose looks right but is at the upper limit for a renally-impaired patient. The renal function was not checked before prescribing.',
    options: [
      'Safe — the dose is within the licensed range.',
      'Prescribing without checking renal function is unsafe and should be escalated.',
      'Appropriate — checking can be done in the morning ward round.',
      'The FY1 should have asked a nurse to check before giving the drug.',
    ],
    answer: 1,
    rationale: 'Renal impairment changes drug pharmacokinetics and is a standard pre-prescribing check. Prescribing without it — even at a "licensed" dose — is unsafe. The drug should be withheld and bloods reviewed before administration, with the on-call registrar notified.',
    gmc: 'GMC Outcomes for Graduates — safe prescribing; practise within competence.',
    mistake: 'Students conflate "within the licensed dose range" with "safe for this patient". Individual patient factors can make even a licensed dose dangerous.',
    script: '"What is the purpose of a renal function check before prescribing? If the bloods come back showing CKD stage 3, how does your answer change? At what point does uncertainty require escalation versus personal action?"',
  },
  {
    domain: 'Colleague performance',
    scenario: 'During a handover, you notice a senior registrar has made a series of unclear entries in the notes for the past two shifts. A nurse also tells you the registrar seemed "not quite right" during last night\'s shift.',
    options: [
      'Document your own handover clearly and leave it at that.',
      'Raise the concern with the clinical lead or another senior colleague.',
      'Speak to the registrar privately first before involving anyone else.',
      'Ask the nurse to file an incident report on your behalf.',
    ],
    answer: 1,
    rationale: 'Potential impairment is a patient safety issue — the threshold for action is lower than for a personal performance concern. Speak privately to the registrar only if safe and if you are confident the conversation won\'t delay action. The primary duty is to ensure patient safety, which requires involving the clinical lead. Option C is appropriate if there are no immediate safety risks; Option B if there are.',
    gmc: 'GMC Domain 3 — working safely with colleagues; raising concerns.',
    mistake: 'Students frequently choose C (private conversation) as a catch-all. That is appropriate for professional shortcomings; impairment/safety concerns require more immediate escalation.',
    script: '"What distinguishes a performance concern from an impairment concern? Would your approach differ if the registrar was a peer rather than a senior? What escalation pathway would you use in your trust?"',
  },
  {
    domain: 'Consent and capacity',
    scenario: 'An 82-year-old man with early dementia refuses a hip replacement that his surgical team believes he needs. His daughter — who holds a health and welfare lasting power of attorney — insists the operation should go ahead.',
    options: [
      'Proceed with the daughter\'s consent — she has LPA.',
      'Refuse to proceed — the patient\'s refusal overrides the LPA.',
      'Perform a formal capacity assessment before any decision.',
      'Seek a second opinion from the patient\'s GP.',
    ],
    answer: 2,
    rationale: 'A welfare LPA only takes effect if the patient lacks capacity — it does not override a capacitous refusal. An early dementia diagnosis does not automatically mean lack of capacity. A formal capacity assessment (Mental Capacity Act 2005) must be performed. If the patient has capacity, his refusal is absolute. If he lacks capacity, the LPA holder can consent but decisions must be in the patient\'s best interests (not necessarily their expressed wishes).',
    gmc: 'GMC Consent guidance — respecting patient autonomy; assessing capacity.',
    mistake: 'Students assume "LPA = automatic authority to consent". LPA welfare powers only activate on loss of capacity.',
    script: '"Walk me through the Mental Capacity Act\'s two-stage test. What would a capacity assessment look like for this patient? If he lacks capacity, is the daughter\'s decision final?"',
  },
  {
    domain: 'Resource allocation',
    scenario: 'A GP has two patients seeking urgent referrals to a cardiology outpatient clinic. One is 74 with stable angina; the other is 38 with atypical chest pain. The clinic has one slot this week.',
    options: [
      'Refer the younger patient — longer potential benefit years ahead.',
      'Refer the older patient — angina is the established diagnosis.',
      'Triage by clinical urgency using standard criteria, not age.',
      'Ask both patients if either can access private care.',
    ],
    answer: 2,
    rationale: 'Allocation decisions must be based on clinical need, not age. The 74-year-old\'s angina may be stable; the 38-year-old\'s atypical chest pain may require urgent investigation to rule out ACS. Neither age nor diagnosis alone determines clinical priority — standardised triage criteria (e.g., two-week-wait rules, NICE chest-pain pathways) apply.',
    gmc: 'GMC Domain 2 — equality and diversity; treating patients fairly.',
    mistake: 'Age-based reasoning (either direction — "younger has more to gain" or "older is sicker") is not a valid clinical criterion on its own.',
    script: '"What triage criteria would you actually apply in primary care chest pain? Does NICE guidance say anything? How do you frame an equity argument when resources are genuinely constrained?"',
  },
  {
    domain: 'Social media',
    scenario: 'A foundation doctor posts on Instagram: "Horrendous 12-hour shift. Can\'t believe patients are treated this way in the NHS." No patient names, no clinical details, but the hospital trust logo is visible in the background.',
    options: [
      'Acceptable — no confidential information shared.',
      'Very inappropriate — undermines public trust and could embarrass the trust.',
      'Somewhat inappropriate — the doctor should delete it soon.',
      'Acceptable — doctors are allowed personal opinions on social media.',
    ],
    answer: 1,
    rationale: 'The GMC social media guidance requires doctors to maintain public trust even on personal accounts. Comments that create a misleading impression of care quality, are identifiable to a specific trust, or undermine confidence in the profession are not protected by "personal opinion". The visible trust branding makes this identifiable. Option B is most appropriate.',
    gmc: 'GMC Domain 4 — maintaining public trust and confidence in the profession.',
    mistake: 'Students often pick D (personal opinions allowed). The GMC is explicit that professional standards do not switch off when you log into personal accounts.',
    script: '"Where does GMC guidance draw the line between legitimate public debate and conduct that undermines trust? Would your answer change if the background was not identifiable?"',
  },
  {
    domain: 'Escalation',
    scenario: 'You are an FY1. A patient\'s NEWS2 score has risen to 7 overnight. You have escalated once to the SHO three hours ago; no review has happened and the SHO is not answering their bleep.',
    options: [
      'Document the failed bleep and wait — the SHO will respond eventually.',
      'Try the SHO once more then escalate directly to the registrar or on-call consultant.',
      'Initiate the critical care outreach team (CCOT/MET) immediately.',
      'Ask a nurse to escalate on your behalf.',
    ],
    answer: 1,
    rationale: 'NEWS2 ≥ 7 is a high-risk score requiring urgent response — in most trusts, this triggers an immediate senior review within minutes, not hours. With no response after one escalation, the correct action is to escalate up the chain directly. Option B is appropriate. If the patient deteriorates further or the score hits critical (many trusts: ≥ 7 immediately), Option C (CCOT) may become the right first step.',
    gmc: 'GMC Outcomes for Graduates — recognise and respond to acutely deteriorating patients.',
    mistake: 'Students conflate "I escalated" with "I have fulfilled my duty". Escalation is not complete until a senior review has occurred.',
    script: '"At what NEWS2 score does your trust mandate a critical care referral? What does the duty-of-care chain look like in your placement? When does "escalating again" become "failure to escalate adequately"?"',
  },
  {
    domain: 'Honesty and probity',
    scenario: 'You made a prescribing error last month that caused a patient mild harm. The patient has since been discharged and recovered fully. The error was not formally reported at the time.',
    options: [
      'Say nothing — the patient recovered and bringing it up will only cause more upset.',
      'Discuss with your educational supervisor and report through the trust\'s incident system.',
      'Tell the patient only if they ask directly.',
      'Report it anonymously so it is documented but you are not identified.',
    ],
    answer: 1,
    rationale: 'The duty of candour requires you to inform patients when something has gone wrong, regardless of outcome. The fact that the patient recovered does not remove the duty to be honest or to report the incident. Anonymous reporting avoids the candour obligation. Option B is correct — speak to your supervisor, then follow the formal incident process, and prepare to have an honest conversation with the patient.',
    gmc: 'GMC Domain 4 — honesty, openness and the duty of candour.',
    mistake: 'Students often pick A or C, reasoning that because the patient recovered the harm threshold for candour was not met. Minor harm (even mild discomfort, extra treatment) still triggers the duty.',
    script: '"What is the legal duty of candour versus the professional duty? Who in the team has the primary responsibility to speak to the patient? How do you approach that conversation?"',
  },
];

export default function TutorSjtCards() {
  const [idx, setIdx] = useState(0);
  const [answerShown, setAnswerShown] = useState(false);
  const [tutorShown, setTutorShown] = useState(false);

  const c = cards[idx];

  const next = () => {
    const nextIdx = (idx + 1) % cards.length;
    setIdx(nextIdx);
    setAnswerShown(false);
    setTutorShown(false);
  };
  const prev = () => {
    const prevIdx = (idx - 1 + cards.length) % cards.length;
    setIdx(prevIdx);
    setAnswerShown(false);
    setTutorShown(false);
  };

  return (
    <>
      <Link to="/tutor-portal" className="back-link"><ArrowLeft width="16" height="16" />Back to tutor portal</Link>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Tutor Portal · SJT Discussion Cards</div>
        <h1>Project, debate, reveal.</h1>
        <p className="subtitle">Project a scenario, let students vote silently, then reveal the GMC-aligned answer with rationale. Designed for 1-to-1 or small group tutoring sessions.</p>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
        <span style={{ padding: '4px 12px', borderRadius: 999, background: '#f3eefc', color: '#5b21b6', fontWeight: 700, fontSize: 12 }}>
          <Shield width="12" height="12" style={{ verticalAlign: 'middle', marginRight: 4 }} />{c.domain}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Card {idx + 1} of {cards.length}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={prev} className="btn" style={{ padding: '8px 12px' }}><ArrowLeft width="14" height="14" /></button>
          <button onClick={next} className="btn btn-primary">Next card <ArrowRight width="14" height="14" /></button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: '24px 26px', background: 'var(--card-soft)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Scenario</div>
        <p style={{ fontSize: 16, lineHeight: 1.65, color: 'var(--text)', margin: 0, fontWeight: 500 }}>{c.scenario}</p>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: '20px 24px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Options — ask students to rate silently first</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {c.options.map((opt, i) => (
            <div key={i} style={{
              padding: '12px 16px', borderRadius: 10, fontSize: 14.5,
              border: `1px solid ${answerShown && i === c.answer ? 'var(--live-green)' : 'var(--border)'}`,
              background: answerShown && i === c.answer ? '#e7f7ef' : '#fff',
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <span style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                background: answerShown && i === c.answer ? 'var(--live-green)' : 'var(--card-soft)',
                color: answerShown && i === c.answer ? '#fff' : 'var(--text-2)',
                display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
              }}>{String.fromCharCode(65 + i)}</span>
              <span style={{ color: 'var(--text)', lineHeight: 1.5 }}>{opt}</span>
            </div>
          ))}
        </div>
        {!answerShown && (
          <button onClick={() => setAnswerShown(true)} className="btn btn-primary" style={{ marginTop: 16 }}>
            <Check width="14" height="14" /> Reveal answer
          </button>
        )}
      </div>

      {answerShown && (
        <div className="card" style={{ marginBottom: 16, padding: '20px 24px', borderLeft: '4px solid var(--live-green)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Answer — Option {String.fromCharCode(65 + c.answer)}</div>
          <p style={{ margin: '0 0 10px', fontSize: 14.5, lineHeight: 1.6, color: 'var(--text)' }}>{c.rationale}</p>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', background: 'var(--card-soft)', padding: '8px 12px', borderRadius: 6 }}>
            {c.gmc}
          </div>
        </div>
      )}

      {answerShown && (
        <div className="card" style={{ marginBottom: 20, padding: '20px 24px', borderLeft: '4px solid #f59e0b', background: '#fffbeb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tutorShown ? 12 : 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Tutor notes (hidden until you click)</div>
            <button onClick={() => setTutorShown((s) => !s)} className="btn" style={{ padding: '6px 12px', fontSize: 12 }}>
              {tutorShown ? 'Hide' : 'Show tutor notes'}
            </button>
          </div>
          {tutorShown && (
            <>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Common student mistake</div>
                <p style={{ margin: 0, fontSize: 13.5, color: '#78350f', lineHeight: 1.55 }}>{c.mistake}</p>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Discussion script</div>
                <p style={{ margin: 0, fontSize: 13.5, color: '#78350f', fontStyle: 'italic', lineHeight: 1.55 }}>{c.script}</p>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
